import { configureAuth } from '@/api/client'
import { api } from '@/api/endpoints'
import { queryKeys } from '@/api/queries'
import type { Meta } from '@/api/types'
import { queryClient } from '@/lib/queryClient'

/**
 * AUTH_MODE=local: the session is an HttpOnly cookie the browser sends by itself,
 * so there is no token to attach. The app shows the sign-in screen whenever
 * `meta.user` is null; this module keeps that in step with the server.
 */

/** Who the cached data belongs to. A different person signing in gets a clean slate. */
let sessionEmail: string | null = null

export function rememberSession(meta: Meta) {
  if (meta.user) sessionEmail = meta.user.email
}

function setMeta(meta: Meta) {
  queryClient.setQueryData(queryKeys.meta, meta)
  rememberSession(meta)
}

/** The session ended server-side (expired, signed out elsewhere, disabled): back to sign-in. */
export function installLocalAuth() {
  configureAuth({
    getToken: async () => null,
    onUnauthorized: () => {
      queryClient.setQueryData<Meta>(queryKeys.meta, (old) => old && { ...old, user: null })
      queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== 'meta' })
    },
  })
}

export async function signIn(email: string, password: string): Promise<void> {
  const { user } = await api.auth.login(email.trim(), password)
  if (sessionEmail && sessionEmail !== user.email) {
    // Someone else's chats may still be in memory: start fresh.
    window.location.replace('/')
    return
  }
  // Departments and features depend on who is signed in.
  setMeta(await api.getMeta())
}

export async function changePassword(current: string, next: string): Promise<void> {
  await api.auth.changePassword(current, next)
  setMeta(await api.getMeta())
}

export async function signOut(): Promise<void> {
  try {
    await api.auth.logout()
  } finally {
    // A full reload drops every cached answer and conversation.
    window.location.replace('/')
  }
}
